import React from 'react';
import { Button, Checkbox, Form, Image, Input, Select, Space, Tag, Tooltip, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { openNotification } from '../../../utils';
import { listQuestionsAll, getQuestion } from '../../questions/services/questionService';
import { createAnswer, updateAnswer, getAnswer } from '../services/answerService';
import { CreateAnswerDto } from '../types/Answer';
import { getReportFormatFunctionTags } from '../../report-formats/services/report-formatService';
import useFunctionTagSuggestions, { NamedFunctionTag } from '../../function-tags/hooks/useFunctionTagSuggestions';
import ImageService from '../../../services/ImageService';

type AnswerFormProps = {
  id?: number;
  onSuccess: () => void;
  referenceId?: number;
  referenceType?: 'question';
  questionType?: string | null;
};

export default function AnswerForm({
  id,
  onSuccess,
  referenceId,
  referenceType = 'question',
  questionType,
}: AnswerFormProps) {
  const [form] = Form.useForm<CreateAnswerDto>();
  const [questionOptions, setQuestionOptions] = React.useState<any[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = React.useState<number | undefined>(referenceId);
  const [selectedQuestionMeta, setSelectedQuestionMeta] = React.useState<any>();
  const [availableFunctionTags, setAvailableFunctionTags] = React.useState<string[]>([]);
  const [attributeFunctionTags, setAttributeFunctionTags] = React.useState<NamedFunctionTag[]>([]);
  const [toolFunctionTags, setToolFunctionTags] = React.useState<NamedFunctionTag[]>([]);
  const [answerImageUrl, setAnswerImageUrl] = React.useState<string | null>(null);
  const [answerImageRemoved, setAnswerImageRemoved] = React.useState(false);
  const [answerImageUploading, setAnswerImageUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const normalizeNullableText = (value?: string | null) => {
    if (value == null) return null;
    return value.trim() === '' ? null : value;
  };
  const isFunctionQuestion = React.useMemo(
    () => (selectedQuestionMeta?.questionType ?? questionType ?? '').toString().toUpperCase() === 'FUNCTION',
    [questionType, selectedQuestionMeta]
  );
  const answerLabel = isFunctionQuestion ? 'Koşul' : 'Cevap';
  const isMultipleQuestion = React.useMemo(
    () => (selectedQuestionMeta?.questionType ?? questionType ?? '').toString().toUpperCase() === 'MULTIPLE',
    [questionType, selectedQuestionMeta]
  );

  const parseImageFields = (value: any): Array<{ title?: string; required?: boolean }> => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value !== 'string' || !value.trim()) {
      return [];
    }
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const serializeImageFields = (value: any): string | null => {
    const rows = Array.isArray(value) ? value : [];
    const normalized = rows
      .map((item: any) => ({
        title: String(item?.title ?? '').trim(),
        required: item?.required === true,
      }))
      .filter((item) => item.title.length > 0);
    return JSON.stringify(normalized);
  };

  const readFileAsDataUrl = (file: RcFile) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAnswerImageBeforeUpload = async (file: RcFile) => {
    if (!file.type?.startsWith('image/')) {
      openNotification('error', 'Hata', 'Yalnızca görsel dosyaları yükleyebilirsiniz.');
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      openNotification('error', 'Hata', 'Cevap görseli 2 MB boyutunu aşamaz.');
      return Upload.LIST_IGNORE;
    }
    setAnswerImageUploading(true);
    try {
      const previewUrl = await readFileAsDataUrl(file);
      const formData = new FormData();
      formData.append('image', file);
      const baseName = file.name.split('.').slice(0, -1).join('.') || 'cevap-gorseli';
      const uniqueName = `${baseName}_${Date.now()}`;
      const response = await ImageService.postImage(formData, uniqueName);
      const imageId = response?.data?.id;
      const imageUrl = response?.data?.preSignUrl ?? response?.data?.imageUrl ?? response?.data?.url ?? previewUrl;
      if (!imageId) {
        openNotification('error', 'Hata', 'Cevap görseli yüklenemedi.');
        return Upload.LIST_IGNORE;
      }
      form.setFieldsValue({ imageId });
      setAnswerImageUrl(imageUrl);
      setAnswerImageRemoved(false);
      openNotification('success', 'Başarılı', 'Cevap görseli yüklendi.');
    } catch {
      openNotification('error', 'Hata', 'Cevap görseli yüklenirken bir hata oluştu.');
    } finally {
      setAnswerImageUploading(false);
    }
    return Upload.LIST_IGNORE;
  };

  const handleAnswerImageRemove = () => {
    form.setFieldsValue({ imageId: undefined });
    setAnswerImageUrl(null);
    setAnswerImageRemoved(true);
  };

  React.useEffect(() => {
    if (id) {
      getAnswer(id).then(d => {
        const data: any = { ...d };
        data.imageFields = parseImageFields(data.imageFields);
        setAnswerImageUrl(data.imageUrl ?? null);
        setAnswerImageRemoved(false);
        let questionId: number | undefined;
        if (data.question) {
          if (typeof data.question === 'object') {
            setSelectedQuestionMeta(data.question);
            questionId = data.question.id;
          } else {
            questionId = data.question;
          }
        }
        const numericId = questionId !== undefined ? Number(questionId) : undefined;
        if (numericId !== undefined && !Number.isNaN(numericId)) {
          data.question = numericId;
          setSelectedQuestionId(numericId);
        } else {
          data.question = undefined;
          setSelectedQuestionId(undefined);
          setSelectedQuestionMeta(undefined);
        }
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      setAnswerImageUrl(null);
      setAnswerImageRemoved(false);
      if (referenceId !== undefined) {
        const numericReference = Number(referenceId);
        const nextReference = Number.isNaN(numericReference) ? undefined : numericReference;
        form.setFieldsValue({ question: nextReference });
        setSelectedQuestionId(nextReference);
      } else {
        setSelectedQuestionId(undefined);
      }
      setSelectedQuestionMeta(undefined);
    }
    listQuestionsAll(undefined, 0, 1000).then(options => {
      setQuestionOptions(options);
    });
  }, [id, form, referenceId, referenceType, questionType]);

  React.useEffect(() => {
    if (!selectedQuestionId) {
      setSelectedQuestionMeta(undefined);
      setAvailableFunctionTags([]);
      setAttributeFunctionTags([]);
      setToolFunctionTags([]);
      form.setFieldsValue({ functionText: undefined });
      return;
    }
    if (selectedQuestionMeta && Number(selectedQuestionMeta?.id) === Number(selectedQuestionId)) {
      return;
    }
    const match = questionOptions.find((item: any) => Number(item?.id) === Number(selectedQuestionId));
    if (match) {
      setSelectedQuestionMeta(match);
      return;
    }
    getQuestion(selectedQuestionId)
      .then(question => {
        setSelectedQuestionMeta(question);
      })
      .catch(() => {
        setSelectedQuestionMeta(undefined);
      });
  }, [form, questionOptions, selectedQuestionId, selectedQuestionMeta]);

  React.useEffect(() => {
    if (selectedQuestionMeta && !isFunctionQuestion) {
      form.setFieldsValue({ functionText: undefined });
    }
  }, [form, isFunctionQuestion, selectedQuestionMeta]);

  React.useEffect(() => {
    const nextTags = new Set<string>();

    const pushTag = (candidate: any) => {
      if (candidate === undefined || candidate === null) {
        return;
      }
      const rawValue = typeof candidate === 'object' ? candidate?.functionTag ?? candidate?.value ?? candidate?.name : candidate;
      const normalized = String(rawValue ?? '').trim();
      if (normalized.length > 0) {
        nextTags.add(normalized);
      }
    };

    if (selectedQuestionMeta) {
      pushTag(selectedQuestionMeta?.functionTag);
      const metaTags = (selectedQuestionMeta as any)?.availableFunctionTags;
      if (Array.isArray(metaTags)) {
        metaTags.forEach(pushTag);
      }
    }

    (questionOptions ?? []).forEach((option: any) => {
      if (!option) {
        return;
      }
      pushTag(option?.functionTag);
      const optionTags = option?.availableFunctionTags;
      if (Array.isArray(optionTags)) {
        optionTags.forEach(pushTag);
      }
    });

    setAvailableFunctionTags(Array.from(nextTags));
  }, [questionOptions, selectedQuestionMeta]);

  React.useEffect(() => {
    const extractNumericId = (value: any): number | undefined => {
      if (value === undefined || value === null) {
        return undefined;
      }
      const candidate = typeof value === 'object' ? value?.id ?? value : value;
      const numeric = Number(candidate);
      return Number.isNaN(numeric) ? undefined : numeric;
    };

    const resolveReportFormatId = (question: any): number | undefined => {
      if (!question) {
        return undefined;
      }
      const node = question?.node;
      if (!node || typeof node !== 'object') {
        return undefined;
      }
      const fromReportFormat = extractNumericId(node?.reportFormat);
      if (fromReportFormat !== undefined) {
        return fromReportFormat;
      }
      return extractNumericId(node?.reportFormatId);
    };

    const reportFormatId = resolveReportFormatId(selectedQuestionMeta);
    if (!isFunctionQuestion || !reportFormatId) {
      setAttributeFunctionTags([]);
      setToolFunctionTags([]);
      return;
    }
    let cancelled = false;
    getReportFormatFunctionTags(reportFormatId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const normalizeStaticTags = (
          items: Array<{ name?: string; functionTag?: string; isActive?: boolean; active?: boolean } | null | undefined>,
        ): NamedFunctionTag[] => {
          const map = new Map<string, NamedFunctionTag>();
          (items ?? []).forEach((item) => {
            if (!item) {
              return;
            }
            const activeFlag = item.isActive ?? item.active;
            if (activeFlag !== undefined && !activeFlag) {
              return;
            }
            const rawTag = String(item.functionTag ?? '').trim();
            const fallbackName = String(item.name ?? '').trim();
            const derivedTag = rawTag.length > 0 ? rawTag : fallbackName.replace(/\s+/g, '').toUpperCase();
            const normalizedTag = derivedTag.trim();
            if (!normalizedTag) {
              return;
            }
            const key = normalizedTag.toUpperCase();
            if (!map.has(key)) {
              map.set(key, {
                name: fallbackName || normalizedTag,
                value: normalizedTag,
              });
            }
          });
          return Array.from(map.values());
        };

        setAttributeFunctionTags(normalizeStaticTags(response?.attributeStaticValues ?? []));
        setToolFunctionTags(normalizeStaticTags(response?.toolStaticValues ?? []));
      })
      .catch(() => {
        if (!cancelled) {
          setAttributeFunctionTags([]);
          setToolFunctionTags([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isFunctionQuestion, selectedQuestionMeta]);

  const { functionTagGroups, appendFunctionTag } = useFunctionTagSuggestions({
    availableFunctionTags,
    attributeFunctionTags,
    toolFunctionTags,
    getFunctionText: () => form.getFieldValue('functionText'),
    setFunctionText: (value: string) => form.setFieldsValue({ functionText: value }),
  });

  const onFinish = async (values: CreateAnswerDto) => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);

    const data: any = { ...values };
    data.abbreviation = normalizeNullableText(data.abbreviation);
    if (referenceId !== undefined) {
      data.question = referenceId;
    }
    if (!isFunctionQuestion) {
      data.functionText = undefined;
    }
    data.imageFields = isMultipleQuestion ? serializeImageFields(data.imageFields) : '';
    if (isMultipleQuestion) {
      data.clearImage = answerImageRemoved;
    } else {
      data.imageId = undefined;
      data.clearImage = false;
    }
    let succeeded = false;
    try {
      if (id) {
        await updateAnswer(id, data);
        openNotification('success', 'Başarılı', `${answerLabel} Güncellendi`);
      } else {
        await createAnswer(data);
        openNotification('success', 'Başarılı', `${answerLabel} Eklendi`);
      }
      succeeded = true;
    } catch (e) {
      openNotification('error', 'Hata', `${answerLabel} işlemi başarısız`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }

    if (succeeded) {
      onSuccess();
    }
  };

  const handleQuestionChange = (value: number) => {
    const numericValue = Number(value);
    setSelectedQuestionId(Number.isNaN(numericValue) ? undefined : numericValue);
    const match = questionOptions.find((item: any) => Number(item?.id) === numericValue);
    setSelectedQuestionMeta(match);
  };

  return (
    <>
      <Form form={form} onFinish={onFinish} layout='vertical'>
        <Form.Item name='title' label='Başlık' rules={[{ required: true }]}> 
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='abbreviation' label='Raporda Görünümü' rules={[{ required: true }]}>
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='description' label='Açıklama'>
          <Input.TextArea rows={3} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='imageId' hidden>
          <Input type='hidden' />
        </Form.Item>
        {isFunctionQuestion && (
          <>
            <Form.Item name='functionText' label='Fonksiyon Metni'>
              <Input style={{ width: '100%' }} />
            </Form.Item>
            {functionTagGroups.length ? (
              <div style={{ marginTop: -8, marginBottom: 16 }}>
                {functionTagGroups.map((group) => (
                  <div key={group.key} style={{ marginTop: 12 }}>
                    <Typography.Text strong type='secondary' style={{ marginRight: 8 }}>
                      {group.title}
                    </Typography.Text>
                    <Space wrap size={8} style={{ marginTop: 8 }}>
                      {group.tags.map((item) => (
                        <Tooltip key={item.key} title={item.insertToken}>
                          <Tag
                            color={group.color === 'default' ? undefined : group.color}
                            onClick={() => appendFunctionTag(item.insertToken)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span>{item.label}</span>
                          </Tag>
                        </Tooltip>
                      ))}
                    </Space>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
        <Form.Item name='isDefault' valuePropName='checked'>
          <Checkbox>Varsayılan mı?</Checkbox>
        </Form.Item>
        <Form.Item name='isTrue' valuePropName='checked'>
          <Checkbox>Doğru mu?</Checkbox>
        </Form.Item>
        <Form.Item name='isDescriptionRequired' valuePropName='checked'>
          <Checkbox>Açıklama Gerekli mi?</Checkbox>
        </Form.Item>
        {isMultipleQuestion ? (
          <Form.Item label='Cevap Görseli'>
            <Space direction='vertical' size={8}>
              <Space size={8} wrap>
                <Upload
                  accept='image/*'
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={handleAnswerImageBeforeUpload}
                >
                  <Button icon={<UploadOutlined />} loading={answerImageUploading}>
                    {answerImageUrl ? 'Görseli Değiştir' : 'Görsel Yükle'}
                  </Button>
                </Upload>
                {answerImageUrl ? (
                  <Button danger icon={<DeleteOutlined />} onClick={handleAnswerImageRemove}>
                    Görseli Kaldır
                  </Button>
                ) : null}
              </Space>
              {answerImageUrl ? (
                <Image
                  src={answerImageUrl}
                  alt='Cevap görseli'
                  width={96}
                  height={72}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              ) : null}
            </Space>
          </Form.Item>
        ) : null}
        {isMultipleQuestion ? (
          <Form.List name='imageFields'>
            {(fields, { add, remove }) => (
              <Form.Item label='Görsel Alanları'>
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space key={field.key} align='baseline' style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'title']}
                        rules={[{ required: true, message: 'Görsel başlığı zorunludur.' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder='Görsel başlığı' />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'required']}
                        valuePropName='checked'
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox>Zorunlu mu?</Checkbox>
                      </Form.Item>
                      <Button danger onClick={() => remove(field.name)}>
                        Sil
                      </Button>
                    </Space>
                  ))}
                  <Button type='dashed' onClick={() => add({ title: '', required: false })} block>
                    Görsel Alanı Ekle
                  </Button>
                </Space>
              </Form.Item>
            )}
          </Form.List>
        ) : null}
        {referenceId === undefined && (
          <Form.Item name='question' label='Soru' rules={[{ required: true }]}> 
            <Select
              showSearch
              filterOption={(input, option) =>
                ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
              }
              placeholder='Seçiniz'
              style={{ width: '100%' }}
              onChange={handleQuestionChange}
            >
              {questionOptions.map(o => (
                <Select.Option key={o.id} value={o.id}>
                  {o.title ?? o.id}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Button htmlType='submit' type='primary' block loading={submitting} disabled={submitting}>
          Kaydet
        </Button>
      </Form>
    </>
  );
}
